import { useMemo, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import { MdCancel, MdCheck, MdCloudUpload } from "react-icons/md";
import { read, utils } from "xlsx";
import { Container, ExcelCellProps } from "types";
import {
  calculateContainerVolumeWeight,
  normalizeExcel,
  normalizeHeader,
  normalizeSchemaKeys,
} from "helpers";
import { useSession } from "providers/SessionProvider";
import { useToast } from "hooks/useToast";
import useCrud from "hooks/useCrud";
import { CONTAINERS_URL } from "constants/urls";
import { toPositiveNumber } from "helpers/formatNumber";
import { validateSchema } from "helpers/schemaValidator";
import { ENTITY } from "constants/entities";
import { STATUS_CODES } from "constants/index";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";

type Props = {};

type NewContainerRow = Record<string, any>;
type NewContainerPayload = Pick<
  Container,
  "description" | "m3_min" | "m3_max" | "volume_weight" | "is_active"
>;

const SHEET_NAME = "CONTENEDORES";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

function parseIsActive(value: any): boolean {
  if (value === undefined || value === null || String(value).trim() === "") return true;

  const v = normalizeHeader(value);

  if (["si", "sí", "s", "true", "1", "yes", "y"].includes(v)) return true;
  if (["no", "false", "0", "n"].includes(v)) return false;

  return false;
}

function findHeaderRowIndex(rows: unknown[][]): number {
  const requiredHeaders = ["descripcion", "m3 minimo", "m3 maximo"];

  return rows.findIndex((row) => {
    const normalizedRow = row.map((cell) => normalizeHeader(cell));

    return requiredHeaders.every((header) => normalizedRow.includes(header));
  });
}

export default function FileImportContainers({}: Props) {
  const [uploadedContainers, setUploadedContainers] = useState<NewContainerPayload[]>([]);
  const [listOfErrors, setListOfErrors] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetInput, setResetInput] = useState(Math.random().toString(36));
  const [error, setError] = useState(false);

  const fileUploadRef = useRef<HTMLInputElement | null>(null);

  const { addSessionData, updateSessionData } = useSession();
  const { showSnackBar } = useToast();

  const { createData: createOrUpdateContainersMassive } = useCrud<any>(
    `${CONTAINERS_URL}/massive`,
    { disableAutoFetch: true }
  );

  const activeAssociations = useMemo(() => {
    return ["SI", "NO", "SÍ", "TRUE", "FALSE", "1", "0"].map(normalizeExcel);
  }, []);

  const containersSchema: Record<string, ExcelCellProps> = {
    "Descripcion": { type: "string", required: true },
    "M3 minimo": { type: "number", required: true },
    "M3 maximo": { type: "number", required: true },
    "Peso volumetrico": { type: "number", required: false },
    "Contenedor activo": { type: "string", required: false, associations: activeAssociations },
  };

  async function createContainerStructure(rows: NewContainerRow[]) {
    const errors: string[] = [];
    const transformed: NewContainerPayload[] = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const line = i + 1;

      const description = String(row["Descripcion"] ?? "").trim();
      if (!description) {
        errors.push(`Fila ${line}: "Descripcion" es requerida.`);
        continue;
      }

      const m3Min = toPositiveNumber(row["M3 minimo"]);
      const m3Max = toPositiveNumber(row["M3 maximo"]);
      const volumeWeight = row["Peso volumetrico"]
        ? toPositiveNumber(row["Peso volumetrico"])
        : calculateContainerVolumeWeight(m3Max);

      if (!Number.isFinite(m3Min) || m3Min <= 0) {
        errors.push(`Fila ${line}: "M3 minimo" debe ser un número > 0.`);
        continue;
      }
      if (!Number.isFinite(m3Max) || m3Max <= 0) {
        errors.push(`Fila ${line}: "M3 maximo" debe ser un número > 0.`);
        continue;
      }
      if (m3Max < m3Min) {
        errors.push(`Fila ${line}: "M3 maximo" no puede ser menor a "M3 minimo".`);
        continue;
      }
      if (!Number.isFinite(volumeWeight) || volumeWeight <= 0) {
        errors.push(`Fila ${line}: "Peso volumetrico" debe ser un número > 0.`);
        continue;
      }

      const rawActive = row["Contenedor activo"];
      let is_active = true;

      if (rawActive !== undefined && rawActive !== null && String(rawActive).trim() !== "") {
        const v = normalizeExcel(rawActive);
        const isValid =
          ["si", "sí", "s", "true", "1", "yes", "y", "no", "false", "0", "n"].includes(v);

        if (!isValid) {
          errors.push(
            `Fila ${line}: "Contenedor activo" inválido ("${rawActive}"). Usá SI/NO, TRUE/FALSE, 1/0.`
          );
          continue;
        }

        is_active = parseIsActive(rawActive);
      }

      transformed.push({
        description,
        m3_min: m3Min,
        m3_max: m3Max,
        volume_weight: volumeWeight,
        is_active,
      });
    }

    return { transformed, errors };
  }

  const inputMimeType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel";

  const readFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    setError(false);
    setListOfErrors([]);
    e.preventDefault();

    try {
      const file = e.target.files?.[0];
      if (!file) {
        setError(true);
        setLoading(false);
        return;
      }

      const reader = new FileReader();

      reader.onload = async (ev) => {
        try {
          const workbook = read(ev.target?.result as ArrayBuffer, { type: "array" });

          const desired = SHEET_NAME.trim().toUpperCase();
          const sheetName = workbook.SheetNames.find(
            (n) => n.trim().toUpperCase() === desired
          );

          if (!sheetName) {
            setError(true);
            setListOfErrors((prev) => [
              ...prev,
              `No se encontró la hoja "${SHEET_NAME}".`,
              `Hojas disponibles: ${workbook.SheetNames.join(", ")}.`,
            ]);
            setModalOpen(true);
            return;
          }

          const previewRows = utils.sheet_to_json(workbook.Sheets[sheetName], {
            header: 1,
            defval: "",
            blankrows: true,
          }) as unknown[][];

          const headerRowIndex = findHeaderRowIndex(previewRows);

          const rawRows = utils.sheet_to_json(workbook.Sheets[sheetName], {
            defval: "",
            range: headerRowIndex >= 0 ? headerRowIndex : 0,
          }) as NewContainerRow[];

          if (!rawRows.length) {
            setError(true);
            setListOfErrors([`La hoja "${sheetName}" no contiene filas para importar.`]);
            setModalOpen(true);
            return;
          }

          const CANONICAL_MAP: Record<string, string> = {
            descripcion: "Descripcion",
            "m3 minimo": "M3 minimo",
            "m3 maximo": "M3 maximo",
            "peso volumetrico": "Peso volumetrico",
            "contenedor activo": "Contenedor activo",
            activo: "Contenedor activo",
          };

          const canonicalRows = rawRows.map((row) => {
            const out: Record<string, any> = {};
            for (const key in row) {
              const nk = normalizeHeader(key);
              const target = CANONICAL_MAP[nk];
              if (target) out[target] = row[key];
            }
            return out as NewContainerRow;
          });

          const normalizedSchema = normalizeSchemaKeys(containersSchema);

          const normalizedRowsForValidation = canonicalRows.map((row) => {
            const r: Record<string, any> = {};
            for (const key in row) {
              r[normalizeHeader(key)] = row[key];
            }
            return r;
          });

          const result = validateSchema(normalizedRowsForValidation, normalizedSchema);

          if (!result.isValid) {
            setListOfErrors(result.errors.map((err) => `${SHEET_NAME}: ${err.message}`));
            setError(true);
            setModalOpen(true);
            return;
          }

          const { transformed, errors } = await createContainerStructure(canonicalRows);

          if (errors.length > 0) {
            setListOfErrors(errors);
            setError(true);
          } else {
            setUploadedContainers(transformed);
          }

          setModalOpen(true);
        } catch (ex: any) {
          setError(true);
          setListOfErrors((prev) => [...prev, `Error procesando archivo: ${ex?.message}`]);
          setModalOpen(true);
        } finally {
          setLoading(false);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (ex: any) {
      setError(true);
      setListOfErrors((prev) => [...prev, ex.message]);
      setLoading(false);
      setModalOpen(true);
    }
  };

  async function handleSubmit() {
    let hasBackendErrors = false;

    try {
      setLoading(true);
      setModalOpen(false);

      const resp = await createOrUpdateContainersMassive(uploadedContainers); 

      if (resp.status === STATUS_CODES.OK) {
        const { created, updated, failed, errors } = resp.data;

        if (created.length > 0) {
          for (const c of created) {
            addSessionData(ENTITY.CONTAINERS, c);
          }
        }

        if (updated.length > 0) {
          for (const c of updated) {
            updateSessionData(ENTITY.CONTAINERS, c);
          }
        }

        if (failed.length === 0) {
          showSnackBar(
            `Se crearon ${created.length} contenedores y se actualizaron ${updated.length} contenedores correctamente.`,
            "success",
          );
        } else {
          hasBackendErrors = true;
          setListOfErrors(errors ?? []);
          setError(true);
          setModalOpen(true);
          showSnackBar(
            `Resultado: ${created.length} creados — ${updated.length} actualizados — ${failed.length} con errores.`,
            "warning",
          );
          return;
        }
      } else {
        showSnackBar(resp.data?.message ?? "Error inesperado", "error");
      }
    } catch (ex: unknown) {
      const message =
        ex instanceof Error ? ex.message : typeof ex === "string" ? ex : "Error inesperado.";
      showSnackBar(message, "error");
    } finally {
      setLoading(false);

      if (!hasBackendErrors) {
        setModalOpen(false);
        setResetInput(Math.random().toString(36));
        setUploadedContainers([]);
      }
    }
  }

  return (
    <>
      <Button component="label" variant="contained" startIcon={<MdCloudUpload />}>
        {loading ? <CircularProgress size={20} /> : "Cargar Contenedores"}
        <VisuallyHiddenInput
          type="file"
          accept={inputMimeType}
          key={resetInput}
          ref={fileUploadRef}
          onChange={readFile}
        />
      </Button>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Paper
          sx={{
            position: "fixed",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            padding: "1rem",
            width: "500px",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Grid container rowGap={2}>
            {!error && uploadedContainers.length > 0 && (
              <>
                <Typography variant="h6">Datos importados correctamente</Typography>
                <Typography>
                  Contenedores a importar{" "}
                  <span style={{ color: "green" }}>{uploadedContainers.length}</span>
                </Typography>

                <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="outlined"
                    startIcon={<MdCancel />}
                    onClick={() => {
                      setModalOpen(false);
                      setUploadedContainers([]);
                    }}
                  >
                    Cancelar
                  </Button>

                  <Button variant="contained" startIcon={<MdCheck />} onClick={handleSubmit}>
                    Cargar Datos
                  </Button>
                </Grid>
              </>
            )}

            {error && (
              <>
                <Typography variant="h5" color="error" textAlign="center">
                  Ocurrió un error
                </Typography>

                <Grid container justifyContent="center" mt={2}>
                  <Grid item xs={12} md={10} lg={8}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                        Se encontraron ({listOfErrors.length}) errores
                      </AccordionSummary>
                      <AccordionDetails>
                        {listOfErrors.map((err, i) => (
                          <Alert key={i} severity="error" sx={{ mb: 1 }}>
                            {err}
                          </Alert>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      </Modal>

      <Backdrop
        open={loading}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 9999,
          color: "#fff",
          backdropFilter: "blur(2px)",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
